import React, { FC, useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../../api';
import { Agent, MiniThreadMatch, ThreadLayoutType } from '@dreamai/types';
import { AgentThread } from '@dreamai/types';
import { GetAgentsResponse, GetAgentThreadsResponse } from '@dreamai/types';

interface ChatSidebarProps {
  layoutType?: ThreadLayoutType;
  hasCanvasAssets?: boolean;
  threadId?: string | null;
  parentThreadId?: string;
  parentThreadIsInternalMonologue?: boolean;
  onOpenCanvas?: () => void;
}
import ThreadViewContainer from './ThreadViewContainer';
import TaskDiscussionPlaceholder from './TaskDiscussionPlaceholder';
import './chat-sidebar.scss';
import ConversationList from './ConversationList';
import { mergeThreads, formatAgentLabel } from './threadUtils';
import ConversationListLoading from './ConversationListLoading';
import ConversationsLoadingError from './errors/ConversationsLoadingError';
import {
  ConversationError,
  ConversationErrorType,
} from './errors/conversationErrors';
import { AxiosResponse } from 'axios';
import ManagerNameListener from './ManagerNameListener';
import ManagerChangeListener from './ManagerChangeListener';
import { useAgent } from '../../../contexts/AgentContext';
import { usePusher } from '../../../contexts/PusherContext';
import { AgentEvents, ThreadActivityUpdateEvent } from '@dreamai/types';
import ChatHeader from '../ChatHeader';

const ChatSidebar: FC<ChatSidebarProps> = ({
  layoutType = 'tab',
  hasCanvasAssets = false,
  threadId: propThreadId,
  parentThreadId: parentThreadIdProp,
  parentThreadIsInternalMonologue,
  onOpenCanvas,
}) => {
  const { getAccessTokenSilently, logout } = useAuth0();

  // Track mobile state to avoid duplicate header
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [agents, setAgents] = useState<Agent[]>([]);
  const [threads, setThreads] = useState<AgentThread[]>([]);
  const {
    selectedAgent,
    setSelectedAgent,
    setAgents: setContextAgents,
  } = useAgent();
  const [agentsLoading, setAgentsLoading] = useState<boolean>(true);
  const [conversationsLoading, setConversationsLoading] =
    useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [recoveryMessage, setRecoveryMessage] = useState<string | undefined>(
    undefined,
  );
  const [conversationsError, setConversationsError] =
    useState<ConversationError | null>(null);
  const [creationMode, setCreationMode] = useState<boolean>(false);
  const [isMainThread, setIsMainThread] = useState<boolean>(false);
  const [isInternalMonologue, setIsInternalMonologue] =
    useState<boolean>(false);
  const [sharePermission, setSharePermission] = useState<string | undefined>(
    undefined,
  );
  const [discussingObject, setDiscussingObject] = useState<string | undefined>(
    undefined,
  );
  const [discussingObjectMatch, setDiscussingObjectMatch] = useState<
    MiniThreadMatch | undefined
  >(undefined);

  // Discussion thread state — when a monologue-parented task creates a discussion thread
  const [discussionThreadId, setDiscussionThreadId] = useState<
    string | undefined
  >(undefined);

  // Get pusher channel for real-time updates
  const pusherContext = usePusher(true);
  const companyChannel = pusherContext?.pusherChannel;

  // Get query parameters
  const agentId = searchParams.get('agent_id');
  const threadId = searchParams.get('thread_id');
  const isNewConversation = searchParams.get('new') === 'true';
  const isReadOnly = searchParams.get('read_only') === 'true';

  // Determine which thread to display in the sidebar:
  // - For tasks with a non-monologue parent: show the parent thread
  // - For tasks with a monologue parent that have a discussion thread: show the discussion thread
  // - For tasks with a monologue parent and no discussion thread: show the placeholder (handled in render)
  const showPlaceholder =
    !!parentThreadIdProp &&
    parentThreadIsInternalMonologue === true &&
    !discussionThreadId;

  const sidebarThreadId = (() => {
    if (!parentThreadIdProp) return threadId; // Not a task — show the URL thread
    if (parentThreadIsInternalMonologue === false) return parentThreadIdProp; // Show parent project thread
    if (discussionThreadId) return discussionThreadId; // Show created discussion thread
    return null; // Will show placeholder
  })();

  // Manager name update listener is handled by the ManagerNameListener component

  // Function to fetch all available agents
  const fetchAgents = async () => {
    try {
      setAgentsLoading(true);
      const token = await getAccessTokenSilently();
      const response = await api.get<GetAgentsResponse>('/agents', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.data.success) {
        setAgents(response.data.agents);
        setContextAgents(response.data.agents); // Also populate the AgentContext
      } else {
        setError('Failed to load agents');
      }
    } catch (err) {
      setError('Error loading agents');
      console.error('Error fetching agents:', err);
    } finally {
      setAgentsLoading(false);
    }
  };

  // Load all available agents when component mounts
  useEffect(() => {
    fetchAgents();
  }, [getAccessTokenSilently]);

  // Update selectedAgent when agentId in URL changes
  useEffect(() => {
    if (!agentId || agents.length === 0) {
      return;
    }

    // Handle special "other" agent ID for shared threads from other users' agents
    if (agentId === 'other') {
      setSelectedAgent({
        value: 'other',
        label: 'Shared with me',
        dreamieImageUrl: undefined,
        predominantColour: undefined,
      });
    } else {
      const agent = agents.find((a: Agent) => a.id === agentId);
      if (agent) {
        setSelectedAgent({
          value: agent.id,
          label: formatAgentLabel(agent),
          dreamieImageUrl: agent.image_url,
          predominantColour: agent.predominant_colour,
        });
      }
    }
  }, [agentId, agents]);

  // Fetch all threads for the user
  const fetchThreads = async () => {
    try {
      // Show conversations loading state
      setConversationsLoading(true);

      const token = await getAccessTokenSilently();

      // Separate try-catch for network errors
      let response: AxiosResponse<GetAgentThreadsResponse>;
      try {
        response = await api.get<GetAgentThreadsResponse>(`/agents/threads`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      } catch (networkErr) {
        // This catch block will only execute for network-related errors
        setConversationsError({
          type: ConversationErrorType.POSSIBLE_NETWORK_ERROR,
          message:
            networkErr instanceof Error
              ? networkErr.message
              : 'Network connection error',
        });
        console.error('Network error fetching threads:', networkErr);
        return false;
      }

      if (response.data.success) {
        // Set all threads
        setThreads(response.data.threads);

        // Clear any previous error
        setConversationsError(null);
      } else {
        setConversationsError({
          type: ConversationErrorType.UNSUCCESSFUL,
          message: 'Failed to load conversations',
        });
      }
    } catch (err) {
      // This will now only catch non-network errors
      setConversationsError({
        type: ConversationErrorType.UNKNOWN,
        message:
          err instanceof Error ? err.message : 'Error loading conversations',
      });
      console.error('Error fetching threads:', err);
    } finally {
      // Always clear the conversations loading state
      setConversationsLoading(false);
    }
  };

  // Load all threads when component mounts
  useEffect(() => {
    fetchThreads();
  }, [getAccessTokenSilently]);

  // Set creation mode based on URL parameter
  useEffect(() => {
    setCreationMode(isNewConversation);
  }, [isNewConversation]);

  // Clear discussing object state when thread changes or entering creation mode
  useEffect(() => {
    if (!threadId || creationMode) {
      setDiscussingObject(undefined);
      setDiscussingObjectMatch(undefined);
    }
  }, [threadId, creationMode]);

  // Listen for thread activity updates (e.g., thread name/description changes)
  useEffect(() => {
    if (!companyChannel) return;

    const handleThreadActivityUpdate = (data: ThreadActivityUpdateEvent) => {
      // Refresh the thread list to get updated names
      fetchThreads();
    };

    companyChannel.bind(
      AgentEvents.THREAD_ACTIVITY_UPDATE,
      handleThreadActivityUpdate,
    );

    return () => {
      companyChannel.unbind(
        AgentEvents.THREAD_ACTIVITY_UPDATE,
        handleThreadActivityUpdate,
      );
    };
  }, [companyChannel, threadId]);

  // Handle thread selection
  const handleThreadSelect = (
    threadId: string,
    agentId?: string,
    readMarker?: string,
  ) => {
    // Update URL with the selected thread
    // Use provided agentId if available, otherwise fall back to selectedAgent
    const params: Record<string, string> = {
      agent_id: agentId || selectedAgent?.value || '',
      thread_id: threadId,
    };

    // Add read marker to URL params for debugging and tab persistence
    if (readMarker) {
      params.read_marker = readMarker;
    }

    setSearchParams(params);
  };

  // Go back to threads list
  const handleBackToThreads = () => {
    // Navigate to /conversations without any query params
    navigate('/conversations');
    setCreationMode(false);
  };

  return (
    <div className='chat-sidebar'>
      <ManagerNameListener
        agents={agents}
        setAgents={setAgents}
        selectedAgent={selectedAgent}
        setSelectedAgent={setSelectedAgent}
      />
      <ManagerChangeListener
        agents={agents}
        setAgents={setAgents}
        selectedAgent={selectedAgent}
        setSelectedAgent={setSelectedAgent}
        fetchAgents={fetchAgents}
      />
      {/* Header shown here when there ARE canvas assets (not on mobile - handled by ChatInterface) */}
      {hasCanvasAssets && !isMobile && (
        <ChatHeader threadId={propThreadId || null} />
      )}
      <div
        className={`chat-sidebar-content ${!threadId && !creationMode ? 'thread-list' : 'conversation'}`}
      >
        {error && <div className='error-message'>{error}</div>}
        {showPlaceholder ? (
          // Monologue-parented task with no discussion thread yet — show placeholder
          <TaskDiscussionPlaceholder
            subThreadId={propThreadId || threadId || ''}
            agentId={selectedAgent?.value}
            agentNickname={selectedAgent?.label}
            agentImageUrl={
              agents.find((a) => a.id === selectedAgent?.value)?.image_url
            }
            onDiscussionCreated={(newThreadId: string) =>
              setDiscussionThreadId(newThreadId)
            }
          />
        ) : threadId || creationMode ? (
          // Show thread messages when a thread is selected or in creation mode
          <ThreadViewContainer
            threadId={sidebarThreadId}
            agentId={selectedAgent?.value}
            agentName={selectedAgent?.label}
            predominantColour={
              agents.find((a) => a.id === selectedAgent?.value)
                ?.predominant_colour
            }
            onThreadCreated={(newThreadId, newAgentId) => {
              setSearchParams({
                agent_id: selectedAgent?.value || '',
                thread_id: newThreadId,
              });
              setCreationMode(false);
            }}
            onThreadDataLoaded={(threadData) => {
              setIsMainThread(threadData.isMainThread);
              setIsInternalMonologue(threadData.isInternalMonologue || false);
              setSharePermission(threadData.sharePermission);
              setDiscussingObject(threadData.discussingObject);
              setDiscussingObjectMatch(threadData.discussingObjectMatch);
            }}
            creationMode={creationMode}
            recoveryMessage={recoveryMessage}
            onRecoveryMessageUsed={(message) => setRecoveryMessage(message)}
            discussingObject={discussingObject}
            discussingObjectMatch={discussingObjectMatch}
            hasWritePermission={
              !sharePermission ||
              sharePermission === 'OWNER' ||
              sharePermission === 'WRITE' ||
              sharePermission === 'ADMIN'
            }
            onOpenCanvas={onOpenCanvas}
            readOnly={isReadOnly}
          />
        ) : // Show threads list when no thread is selected and not in creation mode
        conversationsLoading ? (
          <ConversationListLoading />
        ) : conversationsError ? (
          <ConversationsLoadingError
            errorMessage={conversationsError.message}
            errorType={conversationsError.type}
            onRetry={() => {
              fetchThreads();
            }}
          />
        ) : (
          <ConversationList
            threads={threads}
            selectedThreadId={threadId}
            onThreadSelect={handleThreadSelect}
          />
        )}
      </div>
    </div>
  );
};

export default ChatSidebar;
